<!-- src/components/AppHeader.vue -->
<template>
  <div id="header" class="header">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <div class="logo-div">
            <div class="logo__wrap">
              <!-- Design keeps the menu icon; no sidebar action here -->
              <div class="menu-icon">
                <a id="menu" href="javascript:void(0)" @click.prevent="app.toggleSideMenu()">
                  <i class="fi fi-br-bars-sort"></i>
                </a>
              </div>

              <div class="logo-and-headermenu">
                <div class="logo-icon text-center">
                  <RouterLink to="/">
                    <span class="toggle-theme">
                      <i class="dark-theme" :style="{ display: isDarkMode ? 'inline' : 'none' }">
                        <img :src="images.logoReverse" alt="Mossbets" />
                      </i>
                      <i class="light-theme" :style="{ display: isDarkMode ? 'none' : 'inline' }">
                        <img :src="images.logo" alt="Mossbets" />
                      </i>
                    </span>
                  </RouterLink>
                </div>
              </div>
            </div>

            <div class="header__content">
              <div id="WebHeaderNav">
                <ul class="header-nav">
                  <li>
                    <RouterLink
                      :to="{ name: 'home' }"
                      :class="{ 'active-nav': $route.name === 'home' }"
                      :aria-current="$route.name === 'home' ? 'page' : null"
                      >betting</RouterLink
                    >
                  </li>
                  <li>
                    <RouterLink
                      :to="{ name: 'virtual-lobby' }"
                      :class="{ 'active-nav': $route.name === 'virtual-lobby' }"
                      :aria-current="$route.name === 'virtual-lobby' ? 'page' : null"
                    >Virtuals</RouterLink>
                  </li>
                  <li>
                    <RouterLink
                      :to="{ name: 'crash-games' }"
                      :class="{ 'active-nav': $route.name === 'crash-games' }"
                      :aria-current="$route.name === 'crash-games' ? 'page' : null"
                    >Crash</RouterLink>
                  </li>
                  <li>
                    <RouterLink
                      :to="{ name: 'instant-games' }"
                      :class="{ 'active-nav': $route.name === 'instant-games' }"
                      :aria-current="$route.name === 'instant-games' ? 'page' : null"
                      >Instant</RouterLink>
                  </li>
                  <li>
                    <RouterLink
                      :to="{ name: 'casino-games' }"
                      :class="{ 'active-nav': $route.name === 'casino-games' }"
                      :aria-current="$route.name === 'casino-games' ? 'page' : null"
                      >Casino</RouterLink>
                  </li>
                  <li>
                    <RouterLink
                      :to="{ name: 'promotion' }"
                      :class="{ 'active-nav': $route.name === 'promotion' }"
                      :aria-current="$route.name === 'promotion' ? 'page' : null"
                      >Promotions</RouterLink>
                  </li>
                </ul>
              </div>

              <!-- Hide on any route rendered inside AuthLayout -->
              <div class="header-account order-1 order-lg-last" v-if="!isAuthLayout">
                <div class="search">
                  <RouterLink :to="{ name: 'search' }"><i class="bi bi-search"></i></RouterLink>
                </div>

                <!-- Show deposit button when authenticated -->
                <div class="deposit" v-if="authStore.isAuthenticated" data-bs-toggle="modal" data-bs-target="#DepositModal">
                  <a href="javascript:void(0);">Deposit</a>
                </div>

                <!-- Show betslip and login/register when not authenticated -->
                <template v-else-if="!authStore.isAuthenticated">
                  <div class="header__betslip">
                    <div id="betslip" @click="app.toggleBetSlip">
                      <img id="betslipIcon" :src="images.betslip" v-if="betSlipStore.betCount === 0" />
                      <div id="betslipTicket" v-else>{{ betSlipStore.betCount }}</div>
                    </div>
                    <span class="text">Betslip</span>
                  </div>

                  <div class="access-account">
                    <RouterLink class="register" :to="{name: 'register'}">register</RouterLink>
                    <RouterLink class="login" :to="{name: 'login'}">login</RouterLink>
                  </div>
                </template>
              </div>
            </div>
            <!-- /.header__content -->
          </div>
          <!-- /.logo-div -->
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { CDN } from '@/config/cdn'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'
import { useBetSlipStore } from '@/stores/betSlipStore'

const route = useRoute()
const authStore = useAuthStore()
const app = useAppStore()
const betSlipStore = useBetSlipStore()

// Reactive state for active category
const activeCategory = ref('')

// Theme state
const isDarkMode = ref(true)

// Any matched record with meta.layout === 'auth'
const isAuthLayout = computed(() => route.matched.some((r) => r.meta?.layout === 'auth'))

const images = {
  logo: CDN.images('mossbets-logo.png'),
  logoReverse: CDN.images('mossbets-logo-reverse.png'),
  betslip: 'https://storage.googleapis.com/kironlite/mossbets/icons/betslip.png',
}

// Initialize theme from localStorage and watch for changes
onMounted(() => {
  const savedTheme = localStorage.getItem('theme')
  isDarkMode.value = savedTheme !== 'light'

  // Apply theme class to body
  if (!isDarkMode.value) {
    document.body.classList.add('light-mode')
  }

  // Watch for storage changes (when theme is changed in other components)
  window.addEventListener('storage', handleStorageChange)

  // Watch for custom theme change event
  window.addEventListener('themeChanged', handleThemeChange)
})

onBeforeUnmount(() => {
  window.removeEventListener('storage', handleStorageChange)
  window.removeEventListener('themeChanged', handleThemeChange)
})

function handleStorageChange(e) {
  if (e.key === 'theme') {
    isDarkMode.value = e.newValue !== 'light'
  }
}

function handleThemeChange() {
  const savedTheme = localStorage.getItem('theme')
  isDarkMode.value = savedTheme !== 'light'
}

// Watch route changes to update active category
watch(
  () => route.name,
  (newRouteName) => {
    if (newRouteName === 'virtual-lobby') {
      activeCategory.value = 'Virtuals'
    } else if (newRouteName === 'crash-games') {
      activeCategory.value = 'Crash Games'
    } else {
      activeCategory.value = ''
    }
  },
  { immediate: true },
)
</script>

<style scoped>
/* Add styles to make the div items look like navigation links */
.item {
  display: block;
  padding: 0.5rem 1rem;
  color: #000;
  text-decoration: none;
  cursor: pointer;
  transition:
    color 0.15s ease-in-out,
    background-color 0.15s ease-in-out;
  border-radius: 0.375rem;
}

.item:hover {
  color: #0d6efd;
}

.item.active-nav {
  color: #0d6efd;
  font-weight: 500;
}

/* Dark theme support */
@media (prefers-color-scheme: dark) {
  .item {
    color: #fff;
  }

  .item:hover {
    color: #6ea8fe;
  }

  .item.active-nav {
    color: #6ea8fe;
  }
}
</style>
